/**
 * Ranking tasks by consequence (#283).
 *
 * WHY NOT THE PRIORITY FIELD
 * --------------------------
 * app.TeamTasks has carried `priority` (Low/Medium/High) since V011, and
 * on its own it is not enough: it is set once, when the task is created,
 * by someone guessing at a future they cannot see. A "Medium" treatment
 * that is now nine days overdue outranks a "High" inspection due next
 * week, and no static field can express that.
 *
 * It is not IGNORED either -- a person who marked something High knew
 * something, and throwing that away would be its own arrogance. It is one
 * input of three.
 *
 * THE THREE INPUTS
 * ----------------
 *   consequence   what it costs to be late. A missed treatment window can
 *                 lose the colony; a late routine inspection loses
 *                 nothing. Fixed per kind of work.
 *   time pressure how overdue, weighted HARDER for the kinds where timing
 *                 is the whole point.
 *   hive state    an open issue on the hive the task belongs to. This is
 *                 what makes the same task rank differently on two
 *                 different hives, which is the thing a flat list can
 *                 never do.
 *
 * ON MATCHING FREE TEXT
 * ---------------------
 * task_type is NVARCHAR(100) filled from a user-editable option list, not
 * an enum -- people type "Apivar strips" and "mite wash" and "check on
 * the nuc". So consequence is read from words rather than looked up, and
 * anything unrecognised is treated as routine.
 *
 * That default is deliberate and it is the safe direction: an unknown
 * task ranked low is a task someone scrolls to, while an unknown task
 * ranked high would push a real treatment off the top of the list. The
 * cost of being wrong is not symmetric.
 */

/** Timing IS the point. Late here has consequences that do not undo. */
const CRITICAL = [
  "treat", "treatment", "medicat", "mite", "varroa", "oxalic", "apivar",
  "apiguard", "formic", "thymol", "amitraz", "foulbrood", "afb", "efb",
  "nosema", "beetle", "disease", "queenless", "requeen", "swarm",
];

/** A season will notice. Late here costs yield or work, not the colony. */
const TIME_SENSITIVE = [
  "feed", "syrup", "sugar", "fondant", "patty", "harvest", "extract",
  "super", "split", "combine", "merge", "winter", "wrap", "mouse",
  "entrance reducer", "move", "transport", "nuc", "install",
];

const CONSEQUENCE = { critical: 100, sensitive: 60, routine: 30 };

/** How much each day late adds, by kind. Timing-critical work compounds
 *  fastest, because that is exactly what makes it critical. */
const OVERDUE_WEIGHT = { critical: 6, sensitive: 3, routine: 1 };

const PRIORITY_NUDGE = { High: 12, Medium: 0, Low: -12 };

export function classify(task) {
  const text = `${task?.task_type || ""} ${task?.description || ""}`.toLowerCase();
  if (CRITICAL.some((word) => text.includes(word))) return "critical";
  if (TIME_SENSITIVE.some((word) => text.includes(word))) return "sensitive";
  return "routine";
}

export function daysUntilDue(task) {
  if (!task?.due_date) return null;
  // Date parts only: a bare YYYY-MM-DD parses as UTC midnight and lands a
  // day early in Central time (9e64d40).
  const due = new Date(`${String(task.due_date).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

/**
 * One number, higher is more urgent.
 *
 * Exported mainly so it can be tested and argued with. Nothing should
 * display it: a beekeeper needs to know WHY a task is at the top, and
 * "87" does not say.
 */
export function urgencyScore(task, { issuesByHive = {} } = {}) {
  const kind = classify(task);
  let score = CONSEQUENCE[kind];

  score += PRIORITY_NUDGE[task?.priority] ?? 0;

  const days = daysUntilDue(task);
  if (days !== null && days < 0) {
    // Capped at 30 days. Past a month the task is not "more overdue" in
    // any useful sense -- it is abandoned, and letting it climb forever
    // would let one forgotten task sit on top of the list permanently.
    score += Math.min(Math.abs(days), 30) * OVERDUE_WEIGHT[kind];
  } else if (days !== null && days <= 2) {
    score += 8;
  } else if (days === null) {
    // Undated work is real but unscheduled, and it must not outrank
    // anything with a date on it.
    score -= 20;
  }

  const issue = issuesByHive[task?.hive_id];
  if (issue === "urgent") score += 40;
  else if (issue) score += 20;

  return score;
}

/** Why this task is where it is, in words. The score is never shown. */
export function reasonFor(task, { issuesByHive = {} } = {}) {
  const days = daysUntilDue(task);
  const issue = issuesByHive[task?.hive_id];

  if (days !== null && days < 0) {
    const late = Math.abs(days);
    return {
      label: `${late} day${late === 1 ? "" : "s"} overdue`,
      tone: classify(task) === "routine" ? "warning" : "danger",
    };
  }
  if (issue === "urgent") return { label: "Hive has an urgent issue", tone: "danger" };
  if (days === 0) return { label: "Due today", tone: "warning" };
  if (issue) return { label: "Hive has an open issue", tone: "warning" };
  if (days === null) return { label: "No date", tone: "neutral" };
  return null;
}

export function rankTasks(tasks, options = {}) {
  return [...(tasks || [])].sort((a, b) => {
    const diff = urgencyScore(b, options) - urgencyScore(a, options);
    if (diff !== 0) return diff;
    // Stable and meaningful: the older due date first, undated last.
    const da = daysUntilDue(a);
    const db = daysUntilDue(b);
    if (da === null && db === null) return 0;
    if (da === null) return 1;
    if (db === null) return -1;
    return da - db;
  });
}

/**
 * Three buckets, because dozens of tasks SORTED is still dozens.
 *
 * Only the first is meant to be read on a normal day. Overdue work joins
 * Today rather than getting a bucket of its own -- a task that was due
 * yesterday and one due this morning are the same decision, and a
 * separate "Overdue" pile is a place for things to be filed and ignored.
 */
export function groupTasks(tasks, options = {}) {
  const ranked = rankTasks(tasks, options);
  const groups = { today: [], week: [], later: [] };
  for (const task of ranked) {
    const days = daysUntilDue(task);
    if (days !== null && days <= 0) groups.today.push(task);
    else if (days !== null && days <= 7) groups.week.push(task);
    else groups.later.push(task);
  }
  return groups;
}

/**
 * The same tasks arranged by where the work is.
 *
 * At thirty hives the real unit of work is a TRIP: somebody drives to a
 * yard and does everything there. A globally ranked list answers "where
 * should I go"; this answers "what am I doing once I am there", and they
 * are different questions that deserve different screens rather than one
 * compromise between them.
 *
 * Sites are ordered by their most urgent task, so the yard most worth
 * driving to is first.
 */
export function tasksBySite(tasks, hives, options = {}) {
  const siteOf = new Map();
  for (const hive of hives || []) {
    siteOf.set(hive.hive_id, { site_id: hive.site_id, site_name: hive.site_name });
  }

  const groups = new Map();
  for (const task of rankTasks(tasks, options)) {
    /*
     * The hive's site, or the TASK'S OWN where there is no hive (#393).
     *
     * Since V101 a task can name a yard without naming a colony --
     * "inspect the hives at Site 7" -- and reading the site off an absent
     * hive filed every one of those under "Not at a site", which is the
     * one thing they are not.
     */
    const site = siteOf.get(task.hive_id)
      || (task.site_id != null ? { site_id: task.site_id, site_name: task.site_name } : null);
    // Tasks with no hive are real work -- ordering glass, paying a vendor
    // -- and belong somewhere rather than being dropped.
    const key = site?.site_id ?? "__nosite__";
    if (!groups.has(key)) {
      groups.set(key, {
        site_id: site?.site_id ?? null,
        site_name: site?.site_name ?? "Not at a site",
        tasks: [],
      });
    }
    groups.get(key).tasks.push(task);
  }

  return [...groups.values()].sort((a, b) => {
    const top = (g) => (g.tasks.length ? urgencyScore(g.tasks[0], options) : -Infinity);
    return top(b) - top(a);
  });
}
